import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableContainerWithScrollShadow from 'components/TableContainerWithScrollShadow';
import { deleteRecord, errorText, fetchList, ReauthRequiredError } from '../../registryApi';

const OrganizationsManagement = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [organizations, setOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  // Permanent delete, only for orgs without experiments (the API refuses the rest; CTA
  // staff deactivate those in Django admin). Same confirm pattern as ExperimentsList. The
  // target outlives deleteOpen until the close transition ends, so the text doesn't blank.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const openDelete = (org) => {
    setDeleteTarget(org);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteRecord('/registry/organizations', deleteTarget.id);
      setOrganizations((prev) => prev.filter((o) => o.id !== deleteTarget.id));
      setDeleteOpen(false);
    } catch (e) {
      if (e instanceof ReauthRequiredError) {
        window.location.href = e.reauthUrl;
        return;
      }
      setDeleteError(errorText(e, 'Could not delete this organization.'));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await fetchList('/registry/organizations');
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (org) => {
    navigate(`/projects/${projectId}/registry/admin/organizations/${org.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate(`/projects/${projectId}/registry/admin/organizations/create`);
  };

  const filteredOrganizations = organizations.filter((org) =>
    (org.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
        <TextField
          label="Search"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputLabelProps={{
            shrink: true,
            sx: { color: 'primary.main', fontWeight: 600 },
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="contained" onClick={handleCreateNew} sx={{ py: '8px' }}>
          + Create Organization
        </Button>
      </Box>

      <TableContainerWithScrollShadow>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Experiments</TableCell>
              <TableCell>Users</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrganizations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No organizations found
                </TableCell>
              </TableRow>
            ) : (
              filteredOrganizations.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>
                    <Box sx={{ fontWeight: 500 }}>{org.name}</Box>
                  </TableCell>
                  <TableCell>{org.experiment_count}</TableCell>
                  <TableCell>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.875rem',
                        bgcolor: org.user_count > 0 ? 'primary.main' : 'grey.400',
                        color: 'white',
                      }}
                    >
                      {org.user_count}
                    </Avatar>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(org)}
                      aria-label="Edit"
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {/* The span lets the tooltip explain why the button is disabled. */}
                    <Tooltip
                      title={
                        org.experiment_count > 0
                          ? "Organizations with experiments can't be deleted. Contact CTA staff to deactivate it."
                          : 'Delete organization'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={org.experiment_count > 0}
                          onClick={() => openDelete(org)}
                          aria-label={`Delete ${org.name}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainerWithScrollShadow>
      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onExited: () => {
            setDeleteTarget(null);
            setDeleteError('');
          },
        }}
      >
        <DialogTitle>Delete organization</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{deleteTarget?.name}</strong>? Its{' '}
            {deleteTarget?.user_count} {deleteTarget?.user_count === 1 ? 'member' : 'members'} will be removed
            from it, and any experiment access it was granted is removed too. Their accounts and other
            organization memberships aren&apos;t affected. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationsManagement;
